interface FootnoteDefinition {
  id: string;
  text: string;
}

/**
 * Preprocesses markdown content to convert GFM-style footnotes to a simple end-notes format.
 *
 * This function:
 * 1. Extracts footnote definitions ([^id]: text) from the markdown
 * 2. Replaces footnote references ([^id]) with numbered markers [1], [2], etc.
 * 3. Appends a "Footnotes" section at the end with all definitions
 *
 * @param markdown - The markdown content to preprocess
 * @returns The preprocessed markdown with footnotes converted to end-notes format
 */
export function preprocessFootnotes(markdown: string): string {
  if (!markdown) return markdown;

  const definitions = new Map<string, FootnoteDefinition>();
  const referenceOrder: string[] = [];

  const parts: { content: string; isCode: boolean }[] = [];
  let currentIndex = 0;
  const fenceRegex = /```[\s\S]*?```/g;
  let match;

  while ((match = fenceRegex.exec(markdown)) !== null) {
    if (match.index > currentIndex) {
      parts.push({
        content: markdown.slice(currentIndex, match.index),
        isCode: false,
      });
    }
    parts.push({ content: match[0], isCode: true });
    currentIndex = match.index + match[0].length;
  }

  if (currentIndex < markdown.length) {
    parts.push({ content: markdown.slice(currentIndex), isCode: false });
  }

  const processedParts = parts.map((part) => {
    if (part.isCode) return part.content;

    let content = part.content;
    const lines = content.split("\n");
    const filteredLines: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const defMatch = line.match(/^\[\^([^\]]+)\]:\s*(.*)$/);

      if (defMatch) {
        const id = defMatch[1];
        let text = defMatch[2];

        i++;
        while (i < lines.length && /^(?: {4}|\t)/.test(lines[i])) {
          text += "\n" + lines[i].replace(/^(?: {4}|\t)/, "");
          i++;
        }

        definitions.set(id, { id, text: text.trim() });
      } else {
        filteredLines.push(line);
        i++;
      }
    }

    return filteredLines.join("\n");
  });

  let result = processedParts.join("");

  const referenceParts = result.split(/(```[\s\S]*?```|`[^`]+`)/);

  result = referenceParts
    .map((part, index) => {
      if (index % 2 === 1) return part;

      return part.replace(/(?<!\\)\[\^([^\]]+)\]/g, (_match, id) => {
        if (!referenceOrder.includes(id)) {
          referenceOrder.push(id);
        }
        const refNumber = referenceOrder.indexOf(id) + 1;
        return `[${refNumber}]`;
      });
    })
    .join("");

  if (referenceOrder.length > 0) {
    result += "\n\n---\n\n**Footnotes**\n\n";
    referenceOrder.forEach((id, index) => {
      const def = definitions.get(id);
      const text = def ? def.text : `(definition not found for [^${id}])`;
      result += `${index + 1}. ${text}\n`;
    });
  }

  return result;
}
